import { FormLeavingListener } from '@exam-center/components';
import { EKycVerificationStatus } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  Button,
  Box,
  Divider,
  Flex,
  PaperSection,
  ConfirmationModal,
  Text,
} from '@shared/components';
import { type Prettify } from '@shared/utils';
import { Form, FormProvider, type UseFormReturn } from 'react-hook-form';

import { type ProctoringEkycVerificationFormType } from '../../containers';
import {
  ProctoringEkycFacialVerification,
  type ProctoringEkycFacialVerificationProps,
} from '../proctoring-ekyc-facial-verification';
import {
  ProctoringEkycInformationVerification,
  type ProctoringEkycInformationVerificationProps,
} from '../proctoring-ekyc-information-verification';
import {
  ProctoringEkycVerificationChecklist,
  type ProctoringEkycVerificationChecklistProps,
} from '../proctoring-ekyc-verification-checklist';
import { ProctoringEkycVerificationOutcome } from '../proctoring-ekyc-verification-outcome';

interface ProctoringEkycVerificationFormProps {
  formContext: UseFormReturn<ProctoringEkycVerificationFormType>;
  onSubmit: (data: ProctoringEkycVerificationFormType) => void | Promise<void>;
  slotProps: Prettify<
    ProctoringEkycInformationVerificationProps['slotProps'] &
      ProctoringEkycFacialVerificationProps['slotProps'] &
      ProctoringEkycVerificationChecklistProps['slotProps']
  >;
  modalProps: {
    open: boolean;
    onClose: () => void;
    onOpen: () => void;
    loading: boolean;
  };
  examCenterCode: string;
  examEventId: string;
  disabled?: boolean;
}

export default function ProctoringEkycVerificationForm(
  props: Readonly<ProctoringEkycVerificationFormProps>,
) {
  const {
    formContext,
    onSubmit,
    slotProps,
    modalProps,
    examCenterCode,
    examEventId,
    disabled: shouldDisabled,
  } = props;

  const { trigger, handleSubmit, watch } = formContext;
  const { onClose, onOpen, open, loading } = modalProps;
  const { verificationStatus } = watch();
  const navigate = useNavigate();

  const handleSave = async () => {
    const isValid = await trigger();
    if (isValid) {
      onOpen();
    }
  };
  const handleCancel = () => {
    navigate('/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc', {
      params: {
        examCenterCode: examCenterCode,
        examEventId: examEventId,
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Box sx={{ width: '100%' }}>
        <Form>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              width: '100%',
            })}
          >
            <PaperSection
              title='การยืนยันตัวตน (e-KYC)'
              sx={{
                width: '100%',
                height: 'fit-content',
              }}
            >
              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  gap: theme.spacing(6),
                  p: theme.spacing(6),
                })}
              >
                <ProctoringEkycInformationVerification slotProps={slotProps} />
                <Divider />
                <ProctoringEkycFacialVerification slotProps={slotProps} />
                <Divider />
                <ProctoringEkycVerificationChecklist
                  slotProps={slotProps}
                  disabled={shouldDisabled}
                />
                <Divider />
                <ProctoringEkycVerificationOutcome
                  verificationStatus={verificationStatus}
                  disabled={shouldDisabled}
                />
              </Flex>
            </PaperSection>
            {shouldDisabled ? null : slotProps?.status?.eKycStatus ===
              EKycVerificationStatus.Rejected ? (
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleCancel}
                sx={{
                  alignSelf: 'flex-end',
                }}
              >
                ย้อนกลับ
              </Button>
            ) : (
              <Flex
                sx={(theme) => ({
                  justifyContent: 'flex-end',
                  gap: theme.spacing(2),
                  width: '100%',
                })}
              >
                <Button variant='outlined' color='secondary' onClick={handleCancel}>
                  ยกเลิก
                </Button>
                <Button onClick={handleSave}>บันทึก</Button>
              </Flex>
            )}
          </Flex>
          <FormLeavingListener onSubmit={onSubmit} />
          <ConfirmationModal
            title='ยืนยันการพิจารณา'
            content={
              <Text>
                คุณกำลังจะ{' '}
                <strong>
                  {verificationStatus === 'APPROVED'
                    ? 'อนุมัติการยืนยันตัวตน'
                    : 'ไม่อนุมัติการยืนยันตัวตน'}
                </strong>{' '}
                ของผู้เข้าสอบรายนี้ กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
              </Text>
            }
            onAccept={handleSubmit(onSubmit)}
            onDiscard={onClose}
            onClose={onClose}
            open={open}
            loading={loading}
            discardText='ยกเลิก'
            acceptText='ยืนยัน'
          />
        </Form>
      </Box>
    </FormProvider>
  );
}
